import { useState, useEffect } from "react";
import React from "react";
import Icon from "../components/Icon";
import UnsavedChangesGuard from "../components/UnsavedChangesGuard";
import { fmt, calcEstimateTotal, countItems, generateReportHTML, generateReceiptHTML } from "../utils";
import { useMailStatus, MailStatusBadge, sendPaymentReminder } from "../useFirestore";

const PAYMENT_TEMPLATES = [
  {
    id: "50-25-25",
    label: "50 / 25 / 25",
    milestones: [
      { label: "Acompte",     labelEn: "Deposit",       percentage: 50, dueTrigger: "signing" },
      { label: "Mi-travaux",  labelEn: "Mid-project",   percentage: 25, dueTrigger: "milestone" },
      { label: "Solde final", labelEn: "Final Balance", percentage: 25, dueTrigger: "completion" },
    ]
  },
  {
    id: "50-50",
    label: "50 / 50",
    milestones: [
      { label: "Acompte",     labelEn: "Deposit",       percentage: 50, dueTrigger: "signing" },
      { label: "Solde final", labelEn: "Final Balance", percentage: 50, dueTrigger: "completion" },
    ]
  },
  {
    id: "100",
    label: "100% — Paiement complet",
    milestones: [
      { label: "Paiement complet", labelEn: "Full Payment", percentage: 100, dueTrigger: "completion" },
    ]
  },
  {
    id: "custom",
    label: "Personnalisé / Custom",
    milestones: []
  }
];

function RappelBadge({ invoiceId }) {
  const status = useMailStatus("payment-reminder", invoiceId);
  return <MailStatusBadge status={status} />;
}

const deepCloneSections = (sections) =>
  sections.map((s, i) => ({ id: s.id || `s_${Date.now()}_${i}`, ...s }));

function Invoices({ t, invoices, addInvoice, updateInvoice, deleteInvoice, customers, priceList, pendingOpenId, onPendingConsumed, settings }) {
  const today = new Date().toISOString().split("T")[0];
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [modal, setModal] = useState(null);
  const [form, setForm] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [innerConfirm, setInnerConfirm] = useState(null);
  const [payModal, setPayModal] = useState(null);
  const [reminderSending, setReminderSending] = useState(null);
  const [reminderSent, setReminderSent] = useState(null);
  const [dragSection, setDragSection] = useState(null);
  const [overSection, setOverSection] = useState(null);
  const [isDirty, setIsDirty] = useState(false);
  const [guard, setGuard] = useState(null);
  useEffect(() => { setIsDirty(false); setGuard(null); setAddPayment(false); }, [modal]);
  const tryClose = (closeFn, saveFn) => { if (!isDirty) { closeFn(); return; } setGuard({ close: closeFn, save: saveFn }); };
  const [coForm, setCoForm] = useState(null);
  const [addPayment, setAddPayment] = useState(false);
  const [payForm, setPayForm] = useState({ amount: 0, date: today, method: "cheque", notes: "" });
  const [trackerFilter, setTrackerFilter] = useState(null);
  const [invoiceYear, setInvoiceYear] = useState(() => localStorage.getItem("taf-invoice-year") || String(new Date().getFullYear()));

  // Auto-open newly created invoice from estimate
  useEffect(() => {
    if (pendingOpenId && invoices.length > 0) {
      const inv = invoices.find(i => i.id === pendingOpenId);
      if (inv) { openEdit(inv); onPendingConsumed(); }
    }
  // eslint-disable-next-line
  }, [invoices, pendingOpenId]);

  const sectionTotal = (sec) => sec.price != null ? sec.price : (sec.items||[]).reduce((a,i)=>a+(i.unitPrice||0)*(i.qty||1),0);
  const invSubtotal = (f) => (f.sections||[]).reduce((a,s)=>a+sectionTotal(s),0);
  const coApprovedTotal = (f) => (f.changeOrders||[]).filter(co=>co.status==="approved"||co.status==="manual").reduce((a,co)=>a+(co.amount||0),0);
  const invTotalWithTax = (f) => { const sub=invSubtotal(f)+coApprovedTotal(f); return sub+Math.round(sub*0.05*100)/100+Math.round(sub*0.09975*100)/100; };
  const paymentsTotal = (f) => (f.payments||[]).reduce((a,p)=>a+(p.amount||0),0);
  const balanceOwing = (f) => invTotalWithTax(f) - paymentsTotal(f);
  const getInvYear = (inv) => {
    const d = inv.createdDate || inv.issuedAt;
    if (!d) return null;
    return String(new Date(d).getFullYear());
  };
  const isOverdueInv = (inv) => {
    if (inv.status === "Paid") return false;
    return (inv.paymentSchedule||[]).some(ms => ms.status !== "paid" && ms.dueDate && ms.dueDate < today);
  };
  const isPaidFull = (inv) => paymentsTotal(inv) > 0 && balanceOwing(inv) <= 0.01;
  const matchesTracker = (inv) => {
    if (!trackerFilter) return true;
    if (trackerFilter === "Draft") return inv.status === "Draft";
    if (trackerFilter === "Sent") return inv.status === "Sent" && !isOverdueInv(inv) && !isPaidFull(inv);
    if (trackerFilter === "Overdue") return isOverdueInv(inv);
    if (trackerFilter === "Paid") return inv.status === "Paid" || isPaidFull(inv);
    if (trackerFilter === "Unpublished") return inv.isPublished === false && inv.status !== "Paid";
    if (trackerFilter === "Published") return inv.isPublished === true;
    return true;
  };
  const yearInvoices = invoices.filter(inv => !invoiceYear || getInvYear(inv) === invoiceYear);
  const filtered = yearInvoices.filter(inv => {
    const c = customers.find(x => x.id === inv.customerId);
    return `${inv.title} ${c?c.firstName:""} ${c?c.lastName:""}`.toLowerCase().includes(search.toLowerCase())
      && (!statusFilter || inv.status === statusFilter)
      && matchesTracker(inv);
  });

  const openAdd = () => {
    setForm({ title:"", customerId:"", serviceType:"Hardscape", status:"Draft", isPublished:false, createdDate:today, sections:[], number:`INV-${Math.floor(100000+Math.random()*900000)}` });
    setModal("edit");
  };
  const openEdit = (inv) => {
    setForm({ ...inv, sections: deepCloneSections(inv.sections || []) });
    setModal("edit");
  };
  const saveInv = async () => {
    const r2 = (v) => Math.round((+v || 0) * 100) / 100;
    const cleaned = {
      ...form,
      sections: (form.sections || []).map(s => ({
        ...s, ...(s.price != null ? { price: r2(s.price) } : {}),
      })),
      paymentSchedule: (form.paymentSchedule || []).map(ms => ({
        ...ms,
        amount: r2(ms.amount),
        ...(ms.paidAmount != null ? { paidAmount: r2(ms.paidAmount) } : {}),
      })),
      changeOrders: (form.changeOrders || []).map(co => ({ ...co, amount: r2(co.amount) })),
      payments: (form.payments || []).map(p => ({ ...p, amount: r2(p.amount) })),
    };
    if (!cleaned.id) await addInvoice(cleaned);
    else await updateInvoice(cleaned.id, cleaned);
    setModal(null);
  };

  const printInvoice = (inv) => {
    const customer = customers.find(c => c.id === inv.customerId);
    const html = generateReportHTML("Facture", inv, customer, settings);
    const win = window.open("", "_blank", "width=960,height=800,scrollbars=yes");
    if (win) { win.document.write(html); win.document.close(); }
  };

  const addSection = () => setForm(p=>({...p,sections:[...p.sections,{id:`s${Date.now()}`,title:"New Section",items:[]}]}));
  const addChangeOrderSection = () => setForm(p=>({...p,sections:[...p.sections,{id:`s${Date.now()}`,title:t.invoices.changeOrderLabel,items:[],isChangeOrder:true}]}));
  const addCO = () => { const n=(form.changeOrders||[]).length+1; setCoForm({id:`co_${Date.now()}`,number:`CO-${String(n).padStart(3,"0")}`,description:"",amount:0,status:"pending",requestedAt:new Date().toISOString(),approvedAt:null,approvedBy:null,rejectedAt:null,rejectedBy:null,portalSentAt:null,notes:""}); };
  const saveCO = () => { if(!coForm) return; setForm(p=>({...p,changeOrders:[...(p.changeOrders||[]).filter(co=>co.id!==coForm.id),coForm]})); setCoForm(null); };
  const updateCOStatus = (coId,status) => setForm(p=>({...p,changeOrders:(p.changeOrders||[]).map(co=>co.id===coId?{...co,status,...(status==="approved"||status==="manual"?{approvedAt:new Date().toISOString(),approvedBy:"admin"}:{}),...(status==="rejected"?{rejectedAt:new Date().toISOString()}:{})}:co)}));
  const deleteCO = (coId) => setForm(p=>({...p,changeOrders:(p.changeOrders||[]).filter(co=>co.id!==coId)}));
  const sendCOToPortal = (coId) => setForm(p=>({...p,changeOrders:(p.changeOrders||[]).map(co=>co.id===coId?{...co,portalSentAt:new Date().toISOString()}:co)}));
  const updateSection = (sid,field,value) => setForm(p=>({...p,sections:p.sections.map(s=>s.id===sid?{...s,[field]:value}:s)}));
  const removeSection = (sid) => setForm(p=>({...p,sections:p.sections.filter(s=>s.id!==sid)}));
  const onSectionDragStart = (e, sid) => { setDragSection(sid); e.stopPropagation(); };
  const onSectionDragOver = (e, sid) => { e.preventDefault(); e.stopPropagation(); setOverSection(sid); };
  const onSectionDrop = (e, targetSid) => {
    e.preventDefault(); e.stopPropagation();
    if (!dragSection || dragSection === targetSid) { setDragSection(null); setOverSection(null); return; }
    setForm(p => {
      const secs = [...p.sections];
      const fromIdx = secs.findIndex(s => s.id === dragSection);
      const toIdx = secs.findIndex(s => s.id === targetSid);
      const [moved] = secs.splice(fromIdx, 1);
      secs.splice(toIdx, 0, moved);
      return { ...p, sections: secs };
    });
    setDragSection(null); setOverSection(null);
  };
  const applyTemplate = (templateId) => {
    const tpl = PAYMENT_TEMPLATES.find(x=>x.id===templateId);
    if (!tpl) return;
    const total = invTotalWithTax(form);
    if (tpl.id==="custom") {
      setForm(p=>({...p,paymentSchedule:[{id:`ms_${Date.now()}_0`,label:"Versement 1",percentage:100,amount:total,dueDate:"",dueTrigger:"completion",status:"pending",paidAt:null,paidAmount:null,paymentMethod:"",paymentNotes:"",sentAt:null}]}));
    } else {
      setForm(p=>({...p,paymentSchedule:tpl.milestones.map((m,i)=>({id:`ms_${Date.now()}_${i}`,label:m.label,percentage:m.percentage,amount:Math.round(total*m.percentage/100*100)/100,dueDate:"",dueTrigger:m.dueTrigger,status:"pending",paidAt:null,paidAmount:null,paymentMethod:"",paymentNotes:"",sentAt:null}))}));
    }
  };
  const updateMilestone = (msId,field,value) => setForm(p=>({...p,paymentSchedule:p.paymentSchedule.map(ms=>ms.id===msId?{...ms,[field]:value}:ms)}));
  const recalcMilestoneAmount = (msId,pct) => { const total=invTotalWithTax(form); setForm(p=>({...p,paymentSchedule:p.paymentSchedule.map(ms=>ms.id===msId?{...ms,percentage:pct,amount:Math.round(total*pct/100*100)/100}:ms)})); };
  const addMilestone = () => { const n=(form.paymentSchedule||[]).length; setForm(p=>({...p,paymentSchedule:[...(p.paymentSchedule||[]),{id:`ms_${Date.now()}_${n}`,label:`Versement ${n+1}`,percentage:0,amount:0,dueDate:"",dueTrigger:"completion",status:"pending",paidAt:null,paidAmount:null,paymentMethod:"",paymentNotes:"",sentAt:null}]})); };
  const confirmMarkPaid = async () => {
    if (!payModal) return;
    const {msId,amount,method,date,notes} = payModal;
    const updatedSchedule = (form.paymentSchedule||[]).map(ms=>ms.id===msId?{...ms,status:"paid",paidAt:date,paidAmount:Math.round((parseFloat(amount)||ms.amount)*100)/100,paymentMethod:method,paymentNotes:notes}:ms);
    const allPaid = updatedSchedule.every(ms=>ms.status==="paid");
    const updatedForm = {...form,paymentSchedule:updatedSchedule,...(allPaid?{paidAt:date,status:"Paid"}:{})};
    setForm(updatedForm);
    if (form.id) await updateInvoice(form.id,updatedForm);
    setPayModal(null);
  };
  const sendReminder = async (ms) => {
    const customer = customers.find(c=>c.id===form.customerId);
    if (!customer?.email||!customer?.portalToken) return;
    setReminderSending(ms.id);
    try {
      await sendPaymentReminder(customer,form,ms,settings);
      setReminderSent(ms.id);
      setTimeout(()=>setReminderSent(null),3000);
    } finally {
      setReminderSending(null);
    }
  };

  // Row-level reminder (list view)
  const [rowReminderSending, setRowReminderSending] = useState(null);
  const [rowReminderSent, setRowReminderSent] = useState(null);
  const handleRowReminder = async (inv, ms) => {
    const customer = customers.find(c => c.id === inv.customerId);
    if (!customer?.email || !customer?.portalToken) return;
    setRowReminderSending(inv.id);
    try {
      await sendPaymentReminder(customer, inv, ms, settings);
      setRowReminderSent(inv.id);
      setTimeout(() => setRowReminderSent(null), 3000);
    } finally {
      setRowReminderSending(null);
    }
  };
  const openReceipt = (ms,msIdx) => {
    const customer = customers.find(c=>c.id===form.customerId);
    const html = generateReceiptHTML(form,ms,msIdx,customer,settings);
    const win = window.open("","_blank","width=820,height=720,scrollbars=yes");
    if (win) { win.document.write(html); win.document.close(); }
  };

  return (
    <div>
      <div className="page-header">
        <div className="page-header-row">
          <div><h1>{t.invoices.title}</h1><p>{t.invoices.subtitle}</p></div>
          <button className="btn btn-primary" onClick={openAdd}><Icon name="plus" size={14}/>{t.invoices.newBtn}</button>
        </div>
      </div>
      <div className="page-body">
        {/* Invoice Tracker Bar */}
        {(()=>{
          const isFr = t.invoices?.title==="Factures";
          const years = [...new Set(invoices.map(inv=>getInvYear(inv)).filter(Boolean))].sort((a,b)=>b-a);
          const cards = [
            { key:null,       label:isFr?"Toutes":"All",          match:()=>true },
            { key:"Draft",    label:isFr?"Brouillon":"Draft",     match:inv=>(inv.status||"").toLowerCase()==="draft" },
            { key:"Sent",     label:isFr?"Envoyée":"Sent",        match:inv=>(inv.status||"").toLowerCase()==="sent" },
            { key:"Overdue",  label:isFr?"En retard":"Overdue",   match:inv=>isOverdueInv(inv) },
            { key:"Paid",     label:isFr?"Payée":"Paid",          match:inv=>isPaidFull(inv) },
            { key:"Cancelled",label:isFr?"Annulée":"Cancelled",   match:inv=>(inv.status||"").toLowerCase()==="cancelled" },
          ];
          const nonCancelled = yearInvoices.filter(inv=>(inv.status||"").toLowerCase()!=="cancelled");
          const facture = nonCancelled.reduce((a,inv)=>a+invTotalWithTax(inv),0);
          const encaisse = yearInvoices.reduce((a,inv)=>a+paymentsTotal(inv),0);
          const solde = facture - encaisse;
          return (
            <div style={{marginBottom:16}}>
              <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:8}}>
                <select className="filter-select" style={{fontSize:12,width:"auto"}} value={invoiceYear} onChange={e=>{setInvoiceYear(e.target.value);localStorage.setItem("taf-invoice-year",e.target.value);setTrackerFilter(null);}}>
                  {years.map(y=><option key={y}>{y}</option>)}
                  {!years.includes(invoiceYear)&&<option>{invoiceYear}</option>}
                </select>
              </div>
              <div style={{display:"grid",gridTemplateColumns:"repeat(6,1fr)",gap:8,marginBottom:8}}>
                {cards.map(card=>{
                  const matches = yearInvoices.filter(card.match);
                  const count = matches.length;
                  const total = matches.reduce((a,inv)=>a+invTotalWithTax(inv),0);
                  const isActive = trackerFilter===card.key;
                  return (
                    <div key={String(card.key)} onClick={()=>setTrackerFilter(isActive?null:card.key)} style={{padding:"10px 12px",borderRadius:"var(--radius-sm)",border:"1px solid var(--border)",borderTop:`3px solid ${isActive?"#0d9488":"transparent"}`,background:isActive?"#f0fdfa":"var(--surface)",cursor:"pointer",transition:"background .15s"}}>
                      <div style={{fontSize:11,fontWeight:600,color:isActive?"#0d9488":"var(--text-3)",marginBottom:4,whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>{card.label}</div>
                      <div style={{fontSize:18,fontWeight:800,color:isActive?"#0d9488":"var(--text)"}}>{count}</div>
                      <div style={{fontSize:11,color:"var(--text-3)",marginTop:2}}>{fmt(total)}</div>
                    </div>
                  );
                })}
              </div>
              <div style={{display:"flex",gap:20,padding:"8px 12px",background:"var(--bg-2)",borderRadius:"var(--radius-sm)",fontSize:12,color:"var(--text-3)"}}>
                <span><strong style={{color:"var(--text)"}}>{isFr?"Facturé":"Invoiced"}:</strong> {fmt(facture)}</span>
                <span><strong style={{color:"#16a34a"}}>{isFr?"Encaissé":"Collected"}:</strong> {fmt(encaisse)}</span>
                <span><strong style={{color:solde>0?"#d97706":"var(--text)"}}>{isFr?"Solde ouvert":"Open balance"}:</strong> {fmt(solde)}</span>
              </div>
            </div>
          );
        })()}
        <div className="toolbar">
          <div className="search-wrap"><Icon name="search"/><input className="search-input" placeholder={t.invoices.search} value={search} onChange={e=>setSearch(e.target.value)}/></div>
          <select className="filter-select" value={statusFilter} onChange={e=>setStatusFilter(e.target.value)}><option value="">{t.invoices.allStatuses}</option>{["Draft","Sent","Paid"].map(s=><option key={s}>{s}</option>)}</select>
        </div>
        <div className="card">
          {filtered.map(inv=>{
            const c=customers.find(x=>x.id===inv.customerId);
            return (
              <div key={inv.id} className="list-row">
                <div style={{flex:1}}>
                  <div style={{display:"flex",alignItems:"center",gap:8}}>
                    <span style={{fontWeight:600,fontSize:14}}>{inv.title}</span>
                    <select
                      className="form-select"
                      value={inv.status||"Draft"}
                      onClick={ev=>ev.stopPropagation()}
                      onChange={async ev=>{ ev.stopPropagation(); await updateInvoice(inv.id,{...inv,status:ev.target.value}); }}
                      style={{
                        padding:"2px 20px 2px 8px",fontSize:11,fontWeight:600,height:"auto",
                        background:({Draft:"#f3f4f6",Sent:"#dbeafe",Paid:"#dcfce7"})[inv.status]||"#f3f4f6",
                        color:({Draft:"#6b7280",Sent:"#1d4ed8",Paid:"#166534"})[inv.status]||"#6b7280",
                        border:"none",borderRadius:20,cursor:"pointer",
                      }}
                    >
                      {["Draft","Sent","Paid"].map(s=><option key={s}>{s}</option>)}
                    </select>
                    <span style={{fontSize:12,color:"var(--text-3)"}}>{inv.number}</span>
                    <span style={{
                      fontSize:11,fontWeight:600,padding:"2px 8px",borderRadius:10,
                      background:inv.isPublished?"var(--green-bg)":"var(--bg)",
                      color:inv.isPublished?"var(--green)":"var(--text-3)",
                    }}>{inv.isPublished?t.invoices.published:t.invoices.unpublished}</span>
                  </div>
                  <div style={{display:"flex",gap:12,marginTop:3,fontSize:12,color:"var(--text-3)"}}>
                    {c&&<span>{c.firstName} {c.lastName}</span>}
                    <span>{inv.serviceType}</span>
                    <span className="green-text">{fmt(calcEstimateTotal(inv))}</span>
                    <span>{countItems(inv, 'invoice')} {t.invoices.items}</span>
                    {(()=>{const paid=paymentsTotal(inv);if(!paid)return null;const balance=balanceOwing(inv);return<span style={{fontSize:11,fontWeight:600,color:balance<=0?"var(--green)":"#d97706"}}>{balance<=0?"✓ Soldé":`${fmt(paid)} encaissé`}</span>;})()}
                  </div>
                </div>
                <div className="list-row-actions">
                  {/* Contextual next-step button */}
                  {(()=>{
                    const c=customers.find(x=>x.id===inv.customerId);
                    const overdueMsArr=(inv.paymentSchedule||[]).filter(ms=>ms.status!=="paid"&&ms.dueDate&&ms.dueDate<today).sort((a,b)=>a.dueDate.localeCompare(b.dueDate));
                    const upcomingMsArr=(inv.paymentSchedule||[]).filter(ms=>ms.status!=="paid"&&ms.dueDate&&ms.dueDate>=today).sort((a,b)=>a.dueDate.localeCompare(b.dueDate));
                    const canRemind=!!(c?.email&&c?.portalToken);
                    const sending=rowReminderSending===inv.id;
                    const sent=rowReminderSent===inv.id;
                    if(inv.status==="Draft"&&!inv.isPublished)
                      return <button key="pub" className="btn btn-secondary btn-sm" onClick={()=>updateInvoice(inv.id,{...inv,isPublished:true})} style={{fontSize:11,padding:"3px 10px",whiteSpace:"nowrap"}}>Publier</button>;
                    if(inv.status!=="Paid"&&overdueMsArr.length&&canRemind)
                      return <button key="rappel" className="btn btn-secondary btn-sm" onClick={()=>handleRowReminder(inv,overdueMsArr[0])} disabled={sending} style={{fontSize:11,padding:"3px 10px",whiteSpace:"nowrap",color:"var(--red)",borderColor:"var(--red)"}}>{sent?"✓ Envoyé":sending?"...":"Rappel"}</button>;
                    if(inv.status!=="Paid"&&upcomingMsArr.length&&canRemind)
                      return <button key="remind" className="btn btn-secondary btn-sm" onClick={()=>handleRowReminder(inv,upcomingMsArr[0])} disabled={sending} style={{fontSize:11,padding:"3px 10px",whiteSpace:"nowrap"}}>{sent?"✓ Envoyé":sending?"...":"Envoyer rappel"}</button>;
                    return null;
                  })()}
                  <button
                    className="btn-icon"
                    title={inv.isPublished?t.invoices.unpublishBtn:t.invoices.publishBtn}
                    onClick={()=>updateInvoice(inv.id,{...inv,isPublished:!inv.isPublished})}
                    style={{color:inv.isPublished?"var(--green)":"var(--text-3)"}}
                  ><Icon name="eye" size={14}/></button>
                  <button className="btn-icon" title="Imprimer / Print" onClick={()=>printInvoice(inv)}><Icon name="printer" size={14}/></button>
                  <button className="btn-icon" title={t.common.edit} onClick={()=>openEdit(inv)}><Icon name="edit" size={14}/></button>
                  <button className="btn-icon danger" title={t.common.delete} onClick={()=>setConfirmDelete(inv)}><Icon name="trash" size={14}/></button>
                </div>
              </div>
            );
          })}
          {filtered.length===0&&<div className="empty-state"><div className="empty-icon"><Icon name="invoice" size={36}/></div><div className="empty-title">{t.invoices.title}</div></div>}
        </div>
      </div>

      {/* ── INVOICE EDITOR ── */}
      {modal==="edit"&&form&&(
        <div className="modal-backdrop" onClick={e=>e.target===e.currentTarget&&tryClose(()=>setModal(null),saveInv)}>
          <div className="modal modal-lg">
            <div className="modal-header">
              <div>
                <div style={{display:"flex",alignItems:"center",gap:10}}>
                  <div className="modal-title">{form.id?t.invoices.editTitle:t.invoices.addTitle}</div>
                  {form.estimateId&&<span style={{fontSize:11,color:"var(--text-3)",fontStyle:"italic"}}>{t.invoices.createFrom}</span>}
                </div>
                <div className="modal-sub">{form.number}</div>
              </div>
              <button className="btn-icon" title={t.common.close} onClick={()=>tryClose(()=>setModal(null),saveInv)}><Icon name="x"/></button>
            </div>
            <div className="modal-body" onChange={()=>setIsDirty(true)}>
              <div className="form-grid">
                <div className="form-group"><label>Titre *</label><input className="form-input" value={form.title||""} onChange={e=>setForm(p=>({...p,title:e.target.value}))}/></div>
                <div className="form-grid form-grid-4" style={{gap:10}}>
                  <div className="form-group"><label>Client *</label><select className="form-select" value={form.customerId||""} onChange={e=>setForm(p=>({...p,customerId:e.target.value}))}><option value="">—</option>{customers.map(c=><option key={c.id} value={c.id}>{c.firstName} {c.lastName}</option>)}</select></div>
                  <div className="form-group"><label>Type de service</label><select className="form-select" value={form.serviceType||""} onChange={e=>setForm(p=>({...p,serviceType:e.target.value}))}>{["Hardscape","Landscape","Excavation","Pool","Other"].map(s=><option key={s}>{s}</option>)}</select></div>
                  <div className="form-group"><label>Statut</label><select className="form-select" value={form.status||"Draft"} onChange={e=>setForm(p=>({...p,status:e.target.value}))}>{["Draft","Sent","Paid"].map(s=><option key={s}>{s}</option>)}</select></div>
                  <div className="form-group">
                    <label>Portail</label>
                    <button
                      type="button"
                      className="btn btn-secondary"
                      style={{width:"100%",background:form.isPublished?"var(--green-bg)":"",color:form.isPublished?"var(--green)":""}}
                      onClick={()=>setForm(p=>({...p,isPublished:!p.isPublished}))}
                    >
                      <Icon name="eye" size={12}/>{form.isPublished?t.invoices.unpublishBtn:t.invoices.publishBtn}
                    </button>
                  </div>
                </div>
              </div>
              <div className="divider"/>
              <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:10}}>
                <div style={{fontFamily:"var(--font-display)",fontWeight:600,fontSize:14}}>Éléments de ligne</div>
                <div style={{display:"flex",gap:6}}>
                  <button className="btn btn-secondary btn-sm" onClick={addSection}><Icon name="plus" size={12}/>Ajouter section</button>
                  <button className="btn btn-secondary btn-sm" onClick={addChangeOrderSection} style={{color:"#d97706",borderColor:"#fcd34d"}}><Icon name="changeOrder" size={12}/>{t.invoices.addChangeOrder}</button>
                </div>
              </div>
              {form.sections.map((sec, secIdx)=>(
                <div
                  key={sec.id}
                  className="est-section"
                  onDragOver={e=>onSectionDragOver(e,sec.id)}
                  onDrop={e=>onSectionDrop(e,sec.id)}
                  onDragEnd={()=>{setDragSection(null);setOverSection(null);}}
                  style={{
                    opacity: dragSection===sec.id ? 0.5 : 1,
                    border: overSection===sec.id && dragSection!==sec.id ? "2px dashed var(--red)" : sec.isChangeOrder ? "1px solid #fcd34d" : "",
                    background: sec.isChangeOrder ? "#fffbeb" : "",
                    transition: "opacity .15s",
                  }}
                >
                  <div className="est-section-header" style={sec.isChangeOrder?{background:"#fef3c7"}:{}}>
                    <span draggable onDragStart={e=>onSectionDragStart(e,sec.id)} style={{cursor:"grab",color:"var(--text-3)",display:"flex",alignItems:"center"}} onMouseDown={e=>e.stopPropagation()}>
                      <Icon name="grip" size={14}/>
                    </span>
                    <input
                      type="number" min={1} max={form.sections.length}
                      value={secIdx+1}
                      onClick={e=>e.stopPropagation()}
                      onChange={e=>{
                        const n=Math.max(1,Math.min(form.sections.length,+e.target.value||1))-1;
                        setForm(p=>{const secs=[...p.sections];const[moved]=secs.splice(secIdx,1);secs.splice(n,0,moved);return{...p,sections:secs};});
                      }}
                      style={{width:36,padding:"2px 4px",fontSize:11,border:"1px solid var(--border)",borderRadius:4,textAlign:"center",background:"var(--bg)",color:"var(--text-2)",fontWeight:600}}
                    />
                    {sec.isChangeOrder&&<span style={{fontSize:10,fontWeight:700,color:"#d97706",textTransform:"uppercase",letterSpacing:".06em",display:"flex",alignItems:"center",gap:3}}><Icon name="changeOrder" size={12}/>CO</span>}
                    <input
                      className="form-input"
                      style={{flex:1,border:"none",background:"transparent",fontWeight:600,padding:"0 4px",fontSize:13}}
                      value={sec.title}
                      onClick={e=>e.stopPropagation()}
                      onChange={e=>updateSection(sec.id,"title",e.target.value)}
                    />
                    <input
                      type="number"
                      value={sectionTotal(sec)}
                      onClick={e=>e.stopPropagation()}
                      onChange={e=>updateSection(sec.id,"price",+e.target.value||0)}
                      onFocus={e=>e.target.select()}
                      style={{width:110,padding:"3px 6px",fontSize:13,fontWeight:600,border:"1px solid var(--border)",borderRadius:4,textAlign:"right",background:"var(--bg)",color:"var(--text)"}}
                    />
                    <button className="btn-icon danger" title={t.tooltips.deleteSection} onClick={e=>{e.stopPropagation();setInnerConfirm({type:"section",sid:sec.id,label:sec.title||"cette section"});}}><Icon name="trash" size={13}/></button>
                  </div>
                  <textarea
                    value={sec.description||""}
                    onChange={e=>updateSection(sec.id,"description",e.target.value)}
                    placeholder="Description (optionnelle)"
                    rows={2}
                    style={{display:"block",width:"100%",boxSizing:"border-box",padding:"8px 14px",fontSize:12,color:"var(--text-2)",background:sec.isChangeOrder?"#fffbeb":"var(--bg-2)",border:"none",borderBottom:"1px solid var(--border)",resize:"vertical",fontFamily:"inherit",outline:"none"}}
                  />
                </div>
              ))}
              {form.sections.length>0&&(()=>{
                const baseSub = invSubtotal(form);
                const coAmt = coApprovedTotal(form);
                const sub = baseSub+coAmt;
                const tps = Math.round(sub*0.05*100)/100;
                const tvq = Math.round(sub*0.09975*100)/100;
                const total = sub+tps+tvq;
                return (
                  <div className="est-totals">
                    <div className="est-totals-box">
                      {coAmt!==0&&<div className="est-total-row"><span style={{color:"var(--text-3)"}}>Sous-total (base)</span><span style={{color:"var(--text-3)"}}>{fmt(baseSub)}</span></div>}
                      {coAmt!==0&&<div className="est-total-row"><span style={{color:"#d97706"}}>Avenants approuvés</span><span style={{color:"#d97706"}}>+{fmt(coAmt)}</span></div>}
                      {[{l:"Sous-total",v:fmt(sub)},{l:"TPS (5%)",v:fmt(tps)},{l:"TVQ (9,975%)",v:fmt(tvq)}].map((r,i)=><div key={i} className="est-total-row"><span>{r.l}</span><span>{r.v}</span></div>)}
                      <div className="est-total-row grand"><span>Total</span><span>{fmt(total)}</span></div>
                    </div>
                  </div>
                );
              })()}
              {/* ── CHANGE ORDERS ── */}
              <div className="divider"/>
              <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:10}}>
                <div style={{fontFamily:"var(--font-display)",fontWeight:600,fontSize:14,display:"flex",alignItems:"center",gap:6}}>
                  <Icon name="changeOrder" size={13}/>{t.invoices.changeOrders}
                  {(form.changeOrders||[]).length>0&&<span style={{fontSize:11,fontWeight:700,padding:"2px 7px",borderRadius:10,background:"#fef3c7",color:"#d97706"}}>{(form.changeOrders||[]).length}</span>}
                </div>
                <button type="button" className="btn btn-secondary btn-sm" style={{color:"#d97706",borderColor:"#fcd34d"}} onClick={addCO} disabled={!!coForm}><Icon name="plus" size={12}/>Ajouter</button>
              </div>
              {(form.changeOrders||[]).length>0&&(
                <div style={{marginBottom:8,overflowX:"auto"}}>
                  <table style={{width:"100%",borderCollapse:"collapse",fontSize:12}}>
                    <thead>
                      <tr style={{background:"#fef3c7"}}>
                        {["N°","Description","Montant","Statut","Portail","Actions"].map((h,i)=>(
                          <th key={i} style={{padding:"6px 8px",textAlign:i===2?"right":"left",fontWeight:600,fontSize:11,color:"#92400e",borderBottom:"1px solid #fcd34d",whiteSpace:"nowrap"}}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {(form.changeOrders||[]).map(co=>{
                        const scColors={pending:{bg:"#f3f4f6",c:"#6b7280"},approved:{bg:"#dcfce7",c:"#166534"},rejected:{bg:"#fee2e2",c:"#991b1b"},manual:{bg:"#e0f2fe",c:"#0369a1"}};
                        const sc=scColors[co.status]||scColors.pending;
                        const statusLabel={pending:"En attente",approved:"Approuvé ✓",rejected:"Refusé",manual:"Admin ✓"}[co.status]||co.status;
                        return (
                          <tr key={co.id} style={{borderBottom:"1px solid #fef3c7",background:"#fffbeb"}}>
                            <td style={{padding:"5px 8px",fontWeight:600,whiteSpace:"nowrap"}}>{co.number}</td>
                            <td style={{padding:"5px 8px",maxWidth:200}}>{co.description}</td>
                            <td style={{padding:"5px 8px",textAlign:"right",fontWeight:600,whiteSpace:"nowrap"}}>{fmt(co.amount)}</td>
                            <td style={{padding:"5px 8px",whiteSpace:"nowrap"}}><span style={{display:"inline-block",padding:"2px 8px",borderRadius:10,fontSize:11,fontWeight:600,background:sc.bg,color:sc.c}}>{statusLabel}</span></td>
                            <td style={{padding:"5px 8px",whiteSpace:"nowrap",fontSize:11,color:co.portalSentAt?"var(--green)":"var(--text-3)"}}>{co.portalSentAt?"Envoyé":"—"}</td>
                            <td style={{padding:"5px 8px",textAlign:"right"}}>
                              <div style={{display:"flex",gap:3,justifyContent:"flex-end",alignItems:"center"}}>
                                {co.status==="pending"&&<button type="button" className="btn btn-secondary btn-sm" style={{fontSize:11,color:"#16a34a",borderColor:"#bbf7d0"}} onClick={()=>updateCOStatus(co.id,"approved")}>✓ Approuver</button>}
                                {co.status==="pending"&&<button type="button" className="btn btn-secondary btn-sm" style={{fontSize:11,color:"#0369a1",borderColor:"#bae6fd"}} onClick={()=>updateCOStatus(co.id,"manual")}>Admin</button>}
                                {(co.status==="approved"||co.status==="manual")&&<button type="button" className="btn btn-secondary btn-sm" style={{fontSize:11,color:"#991b1b",borderColor:"#fecaca"}} onClick={()=>updateCOStatus(co.id,"rejected")}>Annuler</button>}
                                {co.status==="pending"&&!co.portalSentAt&&<button type="button" className="btn btn-secondary btn-sm" style={{fontSize:11}} onClick={()=>sendCOToPortal(co.id)}>Portail</button>}
                                <button type="button" className="btn-icon danger" title={t.tooltips.deleteChangeOrder} style={{padding:3}} onClick={()=>deleteCO(co.id)}><Icon name="trash" size={12}/></button>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
              {coForm&&(
                <div style={{border:"1px solid #fcd34d",borderRadius:8,padding:12,background:"#fffbeb",marginBottom:8}}>
                  <div style={{fontWeight:600,fontSize:12,color:"#92400e",marginBottom:8}}>{coForm.number} — Nouvel avenant</div>
                  <div className="form-grid form-grid-2" style={{gap:8,marginBottom:8}}>
                    <div className="form-group" style={{gridColumn:"1/-1"}}><label style={{fontSize:11}}>Description</label><input className="form-input" value={coForm.description} onChange={e=>setCoForm(p=>({...p,description:e.target.value}))}/></div>
                    <div className="form-group"><label style={{fontSize:11}}>Montant ($)</label><input type="number" step="0.01" className="form-input" value={coForm.amount} onChange={e=>setCoForm(p=>({...p,amount:parseFloat(e.target.value)||0}))} onFocus={e=>e.target.select()}/></div>
                    <div className="form-group"><label style={{fontSize:11}}>Statut initial</label><select className="form-select" value={coForm.status} onChange={e=>setCoForm(p=>({...p,status:e.target.value}))}><option value="pending">En attente (portail)</option><option value="manual">Approuvé manuellement</option></select></div>
                  </div>
                  <div style={{display:"flex",gap:6,justifyContent:"flex-end"}}>
                    <button type="button" className="btn btn-secondary btn-sm" onClick={()=>setCoForm(null)}>Annuler</button>
                    <button type="button" className="btn btn-primary btn-sm" onClick={saveCO}>Ajouter</button>
                  </div>
                </div>
              )}
              <div className="divider"/>
              <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:10}}>
                <div style={{fontFamily:"var(--font-display)",fontWeight:600,fontSize:14,display:"flex",alignItems:"center",gap:6}}>
                  <Icon name="calendar" size={13}/>Calendrier de paiement
                </div>
                {(form.paymentSchedule||[]).length>0&&(
                  <div style={{display:"flex",gap:6}}>
                    <button type="button" className="btn btn-secondary btn-sm" onClick={addMilestone}><Icon name="plus" size={12}/>Ajouter versement</button>
                    <button type="button" className="btn btn-secondary btn-sm" style={{color:"var(--text-3)"}} onClick={()=>setForm(p=>({...p,paymentSchedule:[]}))}>Effacer</button>
                  </div>
                )}
              </div>
              {!(form.paymentSchedule?.length>0) ? (
                <div style={{display:"grid",gridTemplateColumns:"repeat(4,1fr)",gap:8}}>
                  {PAYMENT_TEMPLATES.map(tpl=>(
                    <button key={tpl.id} type="button" onClick={()=>applyTemplate(tpl.id)} style={{border:"1px solid var(--border)",borderRadius:8,padding:"10px 8px",background:"var(--bg-2)",cursor:"pointer",textAlign:"center"}}>
                      <div style={{fontWeight:700,fontSize:12,color:"var(--text)"}}>{tpl.label}</div>
                      {tpl.milestones.length>0&&<div style={{fontSize:10,color:"var(--text-3)",marginTop:4}}>{tpl.milestones.map(m=>m.label).join(" · ")}</div>}
                    </button>
                  ))}
                </div>
              ) : (
                <>
                  <div style={{overflowX:"auto"}}>
                    <table style={{width:"100%",borderCollapse:"collapse",fontSize:12}}>
                      <thead>
                        <tr style={{background:"var(--bg-2)"}}>
                          {["Versement","%","Montant","Déclencheur","Date d'échéance","Statut","Actions"].map((h,i)=>(
                            <th key={i} style={{padding:"6px 8px",textAlign:i===1||i===2?"right":"left",fontWeight:600,fontSize:11,color:"var(--text-3)",borderBottom:"1px solid var(--border)",whiteSpace:"nowrap"}}>{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {(form.paymentSchedule||[]).map((ms,msIdx)=>{
                          const scColors={pending:{bg:"#f3f4f6",c:"#6b7280"},sent:{bg:"#dbeafe",c:"#1d4ed8"},paid:{bg:"#dcfce7",c:"#166534"},overdue:{bg:"#fee2e2",c:"#991b1b"}};
                          const sc=scColors[ms.status]||scColors.pending;
                          return (
                            <tr key={ms.id} style={{borderBottom:"1px solid var(--border)"}}>
                              <td style={{padding:"5px 8px"}}><input className="form-input" style={{padding:"2px 6px",fontSize:12,minWidth:90}} value={ms.label} onChange={e=>updateMilestone(ms.id,"label",e.target.value)}/></td>
                              <td style={{padding:"5px 8px",textAlign:"right"}}><input type="number" className="form-input" style={{width:50,padding:"2px 4px",fontSize:12,textAlign:"right"}} value={ms.percentage} onChange={e=>recalcMilestoneAmount(ms.id,+e.target.value||0)} onFocus={e=>e.target.select()}/></td>
                              <td style={{padding:"5px 8px",textAlign:"right",fontWeight:600,whiteSpace:"nowrap"}}>{fmt(ms.amount)}</td>
                              <td style={{padding:"5px 8px"}}><select className="form-select" style={{padding:"2px 6px",fontSize:11,height:"auto"}} value={ms.dueTrigger||"completion"} onChange={e=>updateMilestone(ms.id,"dueTrigger",e.target.value)}><option value="signing">À la signature</option><option value="milestone">Mi-travaux</option><option value="completion">À la complétion</option><option value="custom">Date fixe</option></select></td>
                              <td style={{padding:"5px 8px"}}><input type="date" className="form-input" style={{padding:"2px 4px",fontSize:11}} value={ms.dueDate||""} onChange={e=>updateMilestone(ms.id,"dueDate",e.target.value)}/></td>
                              <td style={{padding:"5px 8px",whiteSpace:"nowrap"}}><span style={{display:"inline-block",padding:"2px 8px",borderRadius:10,fontSize:11,fontWeight:600,background:sc.bg,color:sc.c}}>{ms.status==="paid"?"Payé ✓":ms.status==="sent"?"Envoyé":ms.status==="overdue"?"En retard":"En attente"}</span></td>
                              <td style={{padding:"5px 8px",textAlign:"right"}}>
                                <div style={{display:"flex",gap:3,justifyContent:"flex-end",alignItems:"center"}}>
                                  {(ms.status==="pending"||ms.status==="sent")&&<button type="button" className="btn btn-secondary btn-sm" style={{fontSize:11,whiteSpace:"nowrap"}} onClick={()=>setPayModal({msId:ms.id,amount:ms.amount,method:"cheque",date:today,notes:""})}>Marquer payé</button>}
                                  {(ms.status==="pending"||ms.status==="sent")&&(()=>{
                                    const cust=customers.find(c=>c.id===form.customerId);
                                    const canRemind=!!(cust?.email&&cust?.portalToken);
                                    const isSending=reminderSending===ms.id;
                                    const isSent=reminderSent===ms.id;
                                    return <><button type="button" className="btn btn-secondary btn-sm" style={{fontSize:11,whiteSpace:"nowrap",...(isSent?{color:"#16a34a",borderColor:"#16a34a"}:!canRemind?{opacity:.5}:{})}} disabled={!canRemind||isSending} title={!canRemind?"Client sans email ou sans token de portail":""} onClick={()=>sendReminder(ms)}>{isSending?"Envoi...":isSent?"✓ Envoyé":"Rappel"}</button><RappelBadge invoiceId={form.id}/></>;
                                  })()}
                                  {ms.status==="paid"&&<button type="button" className="btn btn-secondary btn-sm" style={{fontSize:11}} onClick={()=>openReceipt(ms,msIdx)}>Reçu</button>}
                                  <button type="button" className="btn-icon danger" title={t.tooltips.deleteMilestone} style={{padding:3}} onClick={()=>setForm(p=>({...p,paymentSchedule:p.paymentSchedule.filter(x=>x.id!==ms.id)}))}><Icon name="trash" size={12}/></button>
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                        {(()=>{
                          const coTotal=coApprovedTotal(form);
                          if (coTotal<=0) return null;
                          const paid=paymentsTotal(form);
                          const coPaidStatus=paid>=invTotalWithTax(form)-0.01?"paid":"pending";
                          const sc=coPaidStatus==="paid"?{bg:"#dcfce7",c:"#166534"}:{bg:"#fef3c7",c:"#92400e"};
                          return (
                            <tr style={{borderTop:"2px solid var(--border)",background:"#fffbeb"}}>
                              <td style={{padding:"5px 8px",fontStyle:"italic",color:"#92400e",fontSize:12}} colSpan={2}>Avenants approuvés / Approved change orders</td>
                              <td style={{padding:"5px 8px",textAlign:"right",fontWeight:600,whiteSpace:"nowrap",color:"#92400e"}}>{fmt(coTotal)}</td>
                              <td colSpan={2}/>
                              <td style={{padding:"5px 8px",whiteSpace:"nowrap"}}><span style={{display:"inline-block",padding:"2px 8px",borderRadius:10,fontSize:11,fontWeight:600,background:sc.bg,color:sc.c}}>{coPaidStatus==="paid"?"Payé ✓":"En attente"}</span></td>
                              <td/>
                            </tr>
                          );
                        })()}
                      </tbody>
                    </table>
                  </div>
                  {(()=>{
                    const contractTotal=invTotalWithTax(form);
                    const paid=paymentsTotal(form);
                    const solde=balanceOwing(form);
                    return (
                      <div style={{display:"flex",justifyContent:"flex-end",gap:20,marginTop:10,padding:"10px 14px",background:"var(--bg-2)",borderRadius:8}}>
                        <div style={{textAlign:"right"}}><div style={{fontSize:10,color:"var(--text-3)",textTransform:"uppercase",letterSpacing:".06em"}}>Total du contrat</div><div style={{fontWeight:700,fontSize:13}}>{fmt(contractTotal)}</div></div>
                        <div style={{textAlign:"right"}}><div style={{fontSize:10,color:"var(--text-3)",textTransform:"uppercase",letterSpacing:".06em"}}>Encaissé</div><div style={{fontWeight:700,fontSize:13,color:"var(--green)"}}>{fmt(paid)}</div></div>
                        <div style={{textAlign:"right"}}><div style={{fontSize:10,color:"var(--text-3)",textTransform:"uppercase",letterSpacing:".06em"}}>Solde restant</div><div style={{fontWeight:700,fontSize:13,color:solde>0?"var(--red)":"var(--green)"}}>{fmt(solde)}</div></div>
                      </div>
                    );
                  })()}
                  {/* ── PAIEMENTS REÇUS ── */}
                  <div style={{marginTop:20}}>
                    <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:8}}>
                      <div style={{fontFamily:"var(--font-display)",fontWeight:600,fontSize:14}}>Paiements reçus</div>
                      {!addPayment&&<button type="button" className="btn btn-secondary btn-sm" onClick={()=>{setPayForm({amount:Math.max(0,Math.round(balanceOwing(form)*100)/100),date:today,method:"cheque",notes:""});setAddPayment(true);}}><Icon name="plus" size={12}/>Ajouter paiement</button>}
                    </div>
                    {(form.payments||[]).length>0&&(
                      <table style={{width:"100%",borderCollapse:"collapse",fontSize:12,marginBottom:8}}>
                        <thead>
                          <tr style={{background:"var(--bg-2)"}}>
                            {["Date","Mode","Montant","Notes",""].map((h,i)=>(
                              <th key={i} style={{padding:"6px 8px",textAlign:i===2?"right":"left",fontWeight:600,fontSize:11,color:"var(--text-3)",borderBottom:"1px solid var(--border)",whiteSpace:"nowrap"}}>{h}</th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {(form.payments||[]).map((p,i)=>(
                            <tr key={p.id||i} style={{borderBottom:"1px solid var(--border)"}}>
                              <td style={{padding:"5px 8px"}}>{p.date}</td>
                              <td style={{padding:"5px 8px",textTransform:"capitalize"}}>{p.method}</td>
                              <td style={{padding:"5px 8px",textAlign:"right",fontWeight:600,color:"var(--green)"}}>{fmt(p.amount)}</td>
                              <td style={{padding:"5px 8px",color:"var(--text-3)",fontSize:11}}>{p.notes}</td>
                              <td style={{padding:"5px 8px",textAlign:"right"}}><button type="button" className="btn-icon danger" style={{padding:3}} title={t.tooltips.deleteAllocation} onClick={()=>setForm(prev=>({...prev,payments:(prev.payments||[]).filter((_,j)=>j!==i)}))}><Icon name="trash" size={12}/></button></td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    )}
                    {(form.payments||[]).length===0&&!addPayment&&<div style={{fontSize:12,color:"var(--text-3)",padding:"6px 0"}}>Aucun paiement enregistré.</div>}
                    {addPayment&&(
                      <div style={{background:"var(--bg-2)",borderRadius:8,padding:"12px 14px",border:"1px solid var(--border)"}}>
                        <div className="form-grid form-grid-4" style={{gap:10,marginBottom:10}}>
                          <div className="form-group"><label>Montant</label><input type="number" step="0.01" className="form-input" value={payForm.amount} onChange={e=>setPayForm(p=>({...p,amount:+e.target.value||0}))} onFocus={e=>e.target.select()}/></div>
                          <div className="form-group"><label>Date</label><input type="date" className="form-input" value={payForm.date} onChange={e=>setPayForm(p=>({...p,date:e.target.value}))}/></div>
                          <div className="form-group"><label>Mode</label><select className="form-select" value={payForm.method} onChange={e=>setPayForm(p=>({...p,method:e.target.value}))}><option value="cheque">Chèque</option><option value="virement">Virement</option><option value="carte">Carte</option><option value="cash">Argent comptant</option></select></div>
                          <div className="form-group"><label>Notes</label><input className="form-input" value={payForm.notes} onChange={e=>setPayForm(p=>({...p,notes:e.target.value}))} placeholder="Ex: Chèque #4821"/></div>
                        </div>
                        <div style={{display:"flex",gap:6,justifyContent:"flex-end"}}>
                          <button type="button" className="btn btn-secondary btn-sm" onClick={()=>setAddPayment(false)}>Annuler</button>
                          <button type="button" className="btn btn-primary btn-sm" onClick={()=>{
                            const newP={id:`pay_${Date.now()}`,amount:+payForm.amount||0,date:payForm.date,method:payForm.method,notes:payForm.notes};
                            const newPayments=[...(form.payments||[]),newP];
                            const newPaid=newPayments.reduce((a,p)=>a+(p.amount||0),0);
                            const total=invTotalWithTax(form);
                            setForm(prev=>({...prev,payments:newPayments,...(newPaid>=total?{status:"Paid",paidAt:payForm.date}:{})}));
                            setAddPayment(false);
                          }}>Enregistrer</button>
                        </div>
                      </div>
                    )}
                  </div>
                </>
              )}
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={()=>tryClose(()=>setModal(null),saveInv)}>{t.invoices.cancel}</button>
              {form.id&&<button className="btn btn-secondary" onClick={()=>printInvoice(form)}><Icon name="printer" size={14}/>Imprimer</button>}
              <button className="btn btn-primary" onClick={saveInv}>{t.invoices.save}</button>
            </div>
          </div>
        </div>
      )}

      {/* Inner confirm (section delete) */}
      {innerConfirm&&(
        <div className="modal-backdrop" style={{zIndex:300}} onClick={()=>setInnerConfirm(null)}>
          <div className="modal" style={{maxWidth:360}} onClick={e=>e.stopPropagation()}>
            <div className="modal-header"><div className="modal-title">Supprimer</div><button className="btn-icon" title={t.common.close} onClick={()=>setInnerConfirm(null)}><Icon name="x"/></button></div>
            <div className="modal-body"><p style={{margin:0,fontSize:14}}>Supprimer <strong>{innerConfirm.label}</strong>?</p></div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={()=>setInnerConfirm(null)}>Annuler</button>
              <button className="btn btn-danger" onClick={()=>{
                removeSection(innerConfirm.sid);
                setInnerConfirm(null);
              }}>Supprimer</button>
            </div>
          </div>
        </div>
      )}

      {/* Mark-as-paid modal */}
      {payModal&&(
        <div className="modal-backdrop" style={{zIndex:300}} onClick={()=>setPayModal(null)}>
          <div className="modal" style={{maxWidth:420}} onClick={e=>e.stopPropagation()}>
            <div className="modal-header"><div className="modal-title">Marquer comme payé</div><button className="btn-icon" title={t.common.close} onClick={()=>setPayModal(null)}><Icon name="x"/></button></div>
            <div className="modal-body">
              <div className="form-grid form-grid-2" style={{gap:12}}>
                <div className="form-group"><label>Montant reçu</label><input type="number" step="0.01" className="form-input" value={payModal.amount} onChange={e=>setPayModal(p=>({...p,amount:e.target.value}))} onFocus={e=>e.target.select()}/></div>
                <div className="form-group"><label>Mode de paiement</label><select className="form-select" value={payModal.method} onChange={e=>setPayModal(p=>({...p,method:e.target.value}))}><option value="cheque">Chèque</option><option value="virement">Virement</option><option value="carte">Carte</option><option value="cash">Argent comptant</option></select></div>
                <div className="form-group"><label>Date de paiement</label><input type="date" className="form-input" value={payModal.date} onChange={e=>setPayModal(p=>({...p,date:e.target.value}))}/></div>
                <div className="form-group" style={{gridColumn:"1/-1"}}><label>Notes (ex: Chèque #4821)</label><input className="form-input" value={payModal.notes} onChange={e=>setPayModal(p=>({...p,notes:e.target.value}))}/></div>
              </div>
            </div>
            <div className="modal-footer"><button className="btn btn-secondary" onClick={()=>setPayModal(null)}>Annuler</button><button className="btn btn-primary" onClick={confirmMarkPaid}>Confirmer le paiement</button></div>
          </div>
        </div>
      )}

      {/* Confirm delete invoice */}
      {confirmDelete&&(
        <div className="modal-backdrop" onClick={()=>setConfirmDelete(null)}>
          <div className="modal" style={{maxWidth:360}} onClick={e=>e.stopPropagation()}>
            <div className="modal-header"><div className="modal-title">Supprimer facture</div><button className="btn-icon" title={t.common.close} onClick={()=>setConfirmDelete(null)}><Icon name="x"/></button></div>
            <div className="modal-body"><p style={{margin:0,fontSize:14}}>Supprimer <strong>{confirmDelete.title}</strong>?</p></div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={()=>setConfirmDelete(null)}>Annuler</button>
              <button className="btn btn-danger" onClick={async()=>{await deleteInvoice(confirmDelete.id);setConfirmDelete(null);}}>Supprimer</button>
            </div>
          </div>
        </div>
      )}
      {guard&&<UnsavedChangesGuard t={t} onSave={async()=>{await guard.save();setGuard(null);setIsDirty(false);}} onDiscard={()=>{guard.close();setGuard(null);setIsDirty(false);}} onKeepEditing={()=>setGuard(null)}/>}
    </div>
  );
}

export default Invoices;
